import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Users, Plus, Search, Globe, Lock, MessageSquare, 
  FileText, UserPlus, LogOut, Trash2, Send, BookOpen,
  ChevronRight, Crown, Shield, StickyNote, Sparkles,
  Loader2, Edit2, ArrowLeft, Clock, Tag, Wifi, WifiOff, Circle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import useCollabEditor from '../hooks/useCollabEditor';
import { authFetch } from '../utils/authFetch';

const API = process.env.REACT_APP_BACKEND_URL;

const StudyGroupsPage = () => {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('my-groups');


  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, publicRes, invitesRes] = await Promise.all([
        authFetch(`${API}/api/collaboration/groups`),
        authFetch(`${API}/api/collaboration/groups/discover`),
        authFetch(`${API}/api/collaboration/invitations`)
      ]);

      if (groupsRes.ok) {
        const data = await groupsRes.json();
        setGroups(data.groups || []);
      }
      if (publicRes.ok) {
        const data = await publicRes.json();
        setPublicGroups(data.groups || []);
      }
      if (invitesRes.ok) {
        const data = await invitesRes.json();
        setInvitations(data.invitations || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      const res = await authFetch(`${API}/api/collaboration/groups/${groupId}/join`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Joined group successfully!');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to join group');
      }
    } catch (error) {
      toast.error('Error joining group');
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      const res = await authFetch(`${API}/api/collaboration/invitations/${invitationId}/accept`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Invitation accepted!');
        fetchData();
      }
    } catch (error) {
      toast.error('Error accepting invitation');
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      const res = await authFetch(`${API}/api/collaboration/invitations/${invitationId}/decline`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Invitation declined');
        fetchData();
      }
    } catch (error) {
      toast.error('Error declining invitation');
    }
  };

  return (
    <div className="space-y-6" data-testid="study-groups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Study Groups</h1>
          <p className="text-muted-foreground mt-1">Collaborate and learn together</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 rounded-xl gap-2"
          data-testid="create-group-btn"
        >
          <Plus className="w-4 h-4" />
          Create Group
        </Button>
      </div>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card className="p-4 border-2 border-yellow-500/30 bg-yellow-500/5 ">
          <h3 className="font-semibold text-yellow-400 mb-3 flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Pending Invitations ({invitations.length})
          </h3>
          <div className="space-y-2">
            {invitations.map((invite) => (
              <div key={invite.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                <div>
                  <p className="font-medium">{invite.group_name}</p>
                  <p className="text-sm text-muted-foreground">Role: {invite.role}</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => handleAcceptInvitation(invite.id)}
                    className="bg-green-500 hover:bg-green-600 rounded-lg"
                  >
                    Accept
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleDeclineInvitation(invite.id)}
                    className="rounded-lg"
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--isp-glass-border)] pb-2">
        {[
          { id: 'my-groups', label: 'My Groups', icon: Users },
          { id: 'discover', label: 'Discover', icon: Globe }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === tab.id 
                ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                : 'text-muted-foreground hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
        </div>
      ) : activeTab === 'my-groups' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {groups.length === 0 ? (
            <Card className="col-span-full p-12 text-center  border-dashed border-2">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No groups yet</h3>
              <p className="text-muted-foreground mb-4">Create a study group or join one to get started</p>
              <Button onClick={() => setShowCreateModal(true)} className="rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Group
              </Button>
            </Card>
          ) : (
            groups.map((group) => (
              <GroupCard 
                key={group.id} 
                group={group} 
                onSelect={() => setSelectedGroup(group)}
                isOwner={group.owner_id === user?.id}
              />
            ))
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {publicGroups.length === 0 ? (
            <Card className="col-span-full p-12 text-center ">
              <Globe className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No public groups available</h3>
              <p className="text-muted-foreground">Check back later or create your own!</p>
            </Card>
          ) : (
            publicGroups.map((group) => (
              <GroupCard 
                key={group.id} 
                group={group} 
                onJoin={() => handleJoinGroup(group.id)}
                isDiscover
              />
            ))
          )}
        </div>
      )}

      {/* Create Group Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateGroupModal 
            onClose={() => setShowCreateModal(false)} 
            onCreated={() => {
              setShowCreateModal(false);
              fetchData();
            }}
          />
        )}
      </AnimatePresence>

      {/* Group Detail Modal */}
      <AnimatePresence>
        {selectedGroup && (
          <GroupDetailModal 
            group={selectedGroup}
            onClose={() => setSelectedGroup(null)}
            onUpdate={fetchData}
            currentUserId={user?.id}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Group Card Component
const GroupCard = ({ group, onSelect, onJoin, isOwner, isDiscover }) => {
  const getRoleIcon = (role) => {
    if (role === 'owner') return <Crown className="w-3 h-3 text-yellow-400" />;
    if (role === 'admin') return <Shield className="w-3 h-3 text-blue-400" />;
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
    >
      <Card className="p-5  border-2 hover:border-purple-500/30 transition-all cursor-pointer h-full">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            {group.is_public ? (
              <Globe className="w-4 h-4 text-green-400" />
            ) : (
              <Lock className="w-4 h-4 text-yellow-400" />
            )}
            <span className="text-xs text-muted-foreground">
              {group.is_public ? 'Public' : 'Private'}
            </span>
          </div>
          {isOwner && getRoleIcon('owner')}
        </div>
        
        <h3 className="font-bold text-lg mb-2">{group.name}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {group.description || 'No description'}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>{group.member_count} members</span>
          </div>
          
          {isDiscover ? (
            <Button 
              size="sm" 
              onClick={onJoin}
              className="rounded-lg bg-purple-500 hover:bg-purple-600"
            >
              Join
            </Button>
          ) : (
            <Button 
              size="sm" 
              variant="ghost"
              onClick={onSelect}
              className="rounded-lg"
            >
              Open <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

// Create Group Modal
const CreateGroupModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/collaboration/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
                  },
        body: JSON.stringify({ name, description, is_public: isPublic })
      });

      if (res.ok) {
        const data = await res.json();
        const groupId = data.group?.id || data.id;

        // Send invites if emails provided
        const emails = inviteEmails.split(',').map(e => e.trim()).filter(e => e && e.includes('@'));
        for (const email of emails) {
          try {
            await authFetch(`${API}/api/collaboration/groups/${groupId}/invite`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, role: 'member' })
            });
          } catch (err) { console.warn('Invite send error:', err.message); }
        }
        
        toast.success(`Group created!${emails.length > 0 ? ` ${emails.length} invite(s) sent.` : ''}`);
        onCreated();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to create group');
      }
    } catch (error) {
      toast.error('Error creating group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-[var(--isp-glass-border)]  p-6 w-full max-w-md"
      >
        <h2 className="text-2xl font-bold mb-6">Create Study Group</h2>
        
        <div className="space-y-4">
          <div>
            <Label>Group Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., CS 301 Study Squad"
              className="mt-1 rounded-xl"
            />
          </div>
          
          <div>
            <Label>Description (optional)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this group about?"
              className="mt-1 rounded-xl"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
            <div>
              <p className="font-medium">Public Group</p>
              <p className="text-sm text-muted-foreground">Anyone can discover and join</p>
            </div>
            <Switch checked={isPublic} onCheckedChange={setIsPublic} />
          </div>

          <div>
            <Label>Invite Members (optional)</Label>
            <Input
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              placeholder="Enter emails, separated by commas"
              className="mt-1 rounded-xl"
              data-testid="invite-emails-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1">Invitations will be sent after group creation</p>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">
            Cancel
          </Button>
          <Button 
            onClick={handleCreate} 
            disabled={loading}
            className="flex-1 rounded-xl bg-purple-500 hover:bg-purple-600"
          >
            {loading ? 'Creating...' : 'Create Group'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Group Detail Modal
const GroupDetailModal = ({ group, onClose, onUpdate, currentUserId }) => {
  const [activeTab, setActiveTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [resources, setResources] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({ title: '', content: '', tags: [] });
  const [newTag, setNewTag] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [resourceSummary, setResourceSummary] = useState(null);
  const [generatingResourceSummary, setGeneratingResourceSummary] = useState(false);
  const [viewingNote, setViewingNote] = useState(null);
  const [liveEditingNoteId, setLiveEditingNoteId] = useState(null);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [userDocs, setUserDocs] = useState([]);
  const [sharingDoc, setSharingDoc] = useState(false);
  const debounceRef = useRef(null);

  // Real-time collaborative editor hook
  const collab = useCollabEditor(liveEditingNoteId, !!liveEditingNoteId);

  // Sync remote content updates to local form (when not actively editing)
  useEffect(() => {
    if (liveEditingNoteId && collab.isConnected && collab.content) {
      // Only update if the textarea is not focused (user not typing)
      const activeEl = document.activeElement;
      const isTypingContent = activeEl?.getAttribute('data-testid') === 'note-content-input';
      if (!isTypingContent) {
        setNoteForm(prev => ({ ...prev, content: collab.content }));
      }
    }
  }, [collab.content, collab.isConnected, liveEditingNoteId]);

  useEffect(() => {
    if (liveEditingNoteId && collab.isConnected && collab.title) {
      const activeEl = document.activeElement;
      const isTypingTitle = activeEl?.getAttribute('data-testid') === 'note-title-input';
      if (!isTypingTitle) {
        setNoteForm(prev => ({ ...prev, title: collab.title }));
      }
    }
  }, [collab.title, collab.isConnected, liveEditingNoteId]);

  const isOwner = group.owner_id === currentUserId;

  const fetchGroupData = useCallback(async () => {
    setLoading(true);
    try {
      const [messagesRes, resourcesRes, notesRes, summaryRes, resSummaryRes] = await Promise.all([
        authFetch(`${API}/api/collaboration/groups/${group.id}/messages`),
        authFetch(`${API}/api/collaboration/groups/${group.id}/resources`),
        authFetch(`${API}/api/collaboration/groups/${group.id}/notes`),
        authFetch(`${API}/api/collaboration/groups/${group.id}/notes/summary`).catch(() => null),
        authFetch(`${API}/api/collaboration/groups/${group.id}/resources/ai-summary`).catch(() => null),
      ]);

      if (messagesRes.ok) {
        const data = await messagesRes.json();
        setMessages(data.messages || []);
      }
      if (resourcesRes.ok) {
        const data = await resourcesRes.json();
        setResources(data.resources || []);
      }
      if (notesRes.ok) {
        const data = await notesRes.json();
        setNotes(data.notes || []);
      }
      if (summaryRes?.ok) {
        const data = await summaryRes.json();
        if (data.has_summary) setAiSummary(data);
      }
      if (resSummaryRes?.ok) {
        const data = await resSummaryRes.json();
        if (data.has_summary) setResourceSummary(data);
      }
    } catch (error) {
      console.error('Error fetching group data:', error);
    } finally {
      setLoading(false);
    }
  }, [group.id]);

  useEffect(() => {
    fetchGroupData();
  }, [fetchGroupData]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const res = await authFetch(`${API}/api/collaboration/groups/${group.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
                  },
        body: JSON.stringify({ content: newMessage, message_type: 'text' })
      });

      if (res.ok) {
        const msg = await res.json();
        setMessages([...messages, msg]);
        setNewMessage('');
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleSaveNote = async () => {
    if (!noteForm.title.trim()) { toast.error('Enter a note title'); return; }
    setSavingNote(true);
    try {
      const url = editingNote
        ? `${API}/api/collaboration/groups/${group.id}/notes/${editingNote.id}`
        : `${API}/api/collaboration/groups/${group.id}/notes`;
      const method = editingNote ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(noteForm)
      });

      if (res.ok) {
        toast.success(editingNote ? 'Note updated' : 'Note created');
        closeNoteEditor();
        setNoteForm({ title: '', content: '', tags: [] });
        setViewingNote(null);
        fetchGroupData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save note');
      }
    } catch (error) {
      toast.error('Error saving note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const res = await authFetch(`${API}/api/collaboration/groups/${group.id}/notes/${noteId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Note deleted');
        setViewingNote(null);
        fetchGroupData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete note');
      }
    } catch (error) {
      toast.error('Error deleting note');
    }
  };

  const handleViewNote = async (noteId) => {
    try {
      const res = await authFetch(`${API}/api/collaboration/groups/${group.id}/notes/${noteId}`);
      if (res.ok) {
        const data = await res.json();
        setViewingNote(data);
      }
    } catch (error) {
      toast.error('Error loading note');
    }
  };

  const handleAiSummary = async () => {
    setGeneratingSummary(true);
    try {
      const res = await authFetch(`${API}/api/collaboration/groups/${group.id}/notes/ai-summary`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        setAiSummary(data);
        toast.success('AI summary generated!');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to generate summary');
      }
    } catch (error) {
      toast.error('Error generating summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleResourceStudyGuide = async () => {
    setGeneratingResourceSummary(true);
    try {
      const res = await authFetch(`${API}/api/collaboration/groups/${group.id}/resources/ai-summary`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        setResourceSummary(data);
        toast.success('AI Study Guide generated!');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to generate study guide');
      }
    } catch (error) {
      toast.error('Error generating study guide');
    } finally {
      setGeneratingResourceSummary(false);
    }
  };

  const openNoteEditor = (note = null) => {
    if (note) {
      setEditingNote(note);
      setNoteForm({ title: note.title, content: note.content, tags: note.tags || [] });
      setLiveEditingNoteId(note.id); // Start WebSocket for live editing
    } else {
      setEditingNote(null);
      setNoteForm({ title: '', content: '', tags: [] });
      setLiveEditingNoteId(null); // No WebSocket for new notes
    }
    setViewingNote(null);
    setShowNoteEditor(true);
  };

  const closeNoteEditor = () => {
    setShowNoteEditor(false);
    setEditingNote(null);
    setLiveEditingNoteId(null);
  };

  const handleLiveContentChange = (newContent) => {
    setNoteForm(prev => ({ ...prev, content: newContent }));
    if (liveEditingNoteId) {
      collab.sendTyping(true);
      // Debounce WebSocket send
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        collab.sendUpdate(newContent, noteForm.title);
        collab.sendTyping(false);
      }, 300);
    }
  };

  const handleLiveTitleChange = (newTitle) => {
    setNoteForm(prev => ({ ...prev, title: newTitle }));
    if (liveEditingNoteId) {
      collab.sendUpdate(noteForm.content, newTitle);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !noteForm.tags.includes(newTag.trim())) {
      setNoteForm({ ...noteForm, tags: [...noteForm.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) return;

    try {
      const res = await authFetch(`${API}/api/collaboration/groups/${group.id}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
                  },
        body: JSON.stringify({ email: inviteEmail, role: 'member' })
      });

      if (res.ok) {
        toast.success(`Invitation sent to ${inviteEmail}`);
        setInviteEmail('');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to invite');
      }
    } catch (error) {
      toast.error('Error sending invitation');
    }
  };

  const openDocPicker = async () => {
    setShowDocPicker(true);
    try {
      const res = await authFetch(`${API}/api/uploads/files`);
      if (res.ok) {
        const data = await res.json();
        // API returns list directly or {files: [...]}
        setUserDocs(Array.isArray(data) ? data : (data.files || []));
      }
    } catch (err) { console.warn('Load docs error:', err.message); setUserDocs([]); }
  };

  const shareDocToGroup = async (fileId) => {
    setSharingDoc(true);
    try {
      const res = await authFetch(`${API}/api/collaboration/groups/${group.id}/resources?file_id=${fileId}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Document shared to group!');
        setShowDocPicker(false);
        fetchGroupData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to share');
      }
    } catch (err) {
      toast.error('Error sharing document');
      console.warn('Share doc error:', err.message);
    } finally {
      setSharingDoc(false);
    }
  };

  const leaveGroup = async () => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;

    try {
      const res = await authFetch(`${API}/api/collaboration/groups/${group.id}/leave`, {
        method: 'POST'
      });

      if (res.ok) {
        toast.success('Left group');
        onClose();
        onUpdate();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to leave group');
      }
    } catch (error) {
      toast.error('Error leaving group');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-[var(--isp-glass-border)]  w-full max-w-2xl max-h-[80vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-4 border-b border-[var(--isp-glass-border)]">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold">{group.name}</h2>
              <p className="text-sm text-muted-foreground">{group.member_count} members</p>
            </div>
            <div className="flex gap-2">
              {!isOwner && (
                <Button size="sm" variant="outline" onClick={leaveGroup} className="rounded-lg text-red-400">
                  <LogOut className="w-4 h-4 mr-1" /> Leave
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={onClose} className="rounded-lg">✕</Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {[
              { id: 'chat', label: 'Chat', icon: MessageSquare },
              { id: 'notes', label: 'Notes', icon: StickyNote },
              { id: 'resources', label: 'Shared Files', icon: FileText },
              { id: 'members', label: 'Members', icon: Users }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setShowNoteEditor(false); setViewingNote(null); }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                  activeTab === tab.id 
                    ? 'bg-purple-500/20 text-purple-400' 
                    : 'text-muted-foreground hover:text-[var(--isp-heading)]'
                }`}
                data-testid={`group-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'notes' && notes.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/30">{notes.length}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500" />
            </div>
          ) : activeTab === 'chat' ? (
            <div className="space-y-3">
              {messages.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No messages yet. Start the conversation!</p>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className="p-3 bg-white/5 rounded-xl">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm">{msg.user_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(msg.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                ))
              )}
            </div>
          ) : activeTab === 'resources' ? (
            <div className="space-y-3" data-testid="resources-tab">
              {/* Action Buttons */}
              <div className="flex gap-2 flex-wrap">
                <Button onClick={openDocPicker} className="rounded-xl gap-2 bg-[#002FA7] hover:bg-[#00227A]" data-testid="add-document-btn">
                  <Plus className="w-4 h-4" /> Add Document
                </Button>
                {resources.length > 0 && (
                  <Button
                    onClick={handleResourceStudyGuide}
                    disabled={generatingResourceSummary}
                    className="rounded-xl gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                    data-testid="ai-study-guide-btn"
                  >
                    {generatingResourceSummary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {generatingResourceSummary ? 'Generating...' : 'AI Study Guide'}
                  </Button>
                )}
              </div>

              {/* Document Picker Dialog */}
              {showDocPicker && (
                <div className="p-4 bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] rounded-xl space-y-3" data-testid="doc-picker">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-[var(--isp-heading)]">Select a document to share</p>
                    <button onClick={() => setShowDocPicker(false)} className="text-[var(--isp-muted)] hover:text-[var(--isp-heading)]">&times;</button>
                  </div>
                  {userDocs.length === 0 ? (
                    <p className="text-xs text-[var(--isp-muted)] text-center py-4">No documents found. Upload documents first in My Documents.</p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {userDocs.map(doc => (
                        <button key={doc.id} onClick={() => !sharingDoc && shareDocToGroup(doc.id)} disabled={sharingDoc}
                          className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 text-left transition-all" data-testid={`pick-doc-${doc.id}`}>
                          <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                          <span className="text-sm text-[var(--isp-heading)] truncate flex-1">{doc.original_filename || doc.filename}</span>
                          <span className="text-[10px] text-[var(--isp-muted)]">{doc.extension}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* AI Study Guide Card */}
              {resourceSummary?.summary && (
                <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl" data-testid="resource-study-guide-card">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <p className="text-sm font-medium text-amber-300">
                      AI Study Guide ({resourceSummary.resource_count} resources)
                    </p>
                    <span className="text-[10px] text-[var(--isp-muted)] ml-auto">
                      {resourceSummary.generated_at?.slice(0, 10)}
                    </span>
                  </div>
                  <div className="text-sm text-[var(--isp-body)] whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto prose-sm">
                    {resourceSummary.summary}
                  </div>
                </div>
              )}

              {resources.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No shared files yet. Share documents from your File Manager!</p>
              ) : (
                resources.map((res) => {
                  const isFile = res.type === 'file';
                  const title = res.title || res.lecture_title || 'Untitled';
                  return (
                    <div key={res.id} className="p-3 bg-white/5 rounded-xl flex items-center gap-3" data-testid={`shared-resource-${res.id}`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isFile ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>
                        {isFile ? <FileText className="w-4 h-4" /> : <BookOpen className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate text-[var(--isp-heading)]">{title}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>Shared by {res.shared_by_name}</span>
                          {isFile && res.file_size && (
                            <><span className="w-1 h-1 rounded-full bg-current" /><span>{(res.file_size / 1024).toFixed(0)} KB</span></>
                          )}
                          {isFile && res.has_study_plan && (
                            <span className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 text-[10px] font-medium">Analyzed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : activeTab === 'notes' ? (
            <div className="space-y-4" data-testid="notes-tab">
              {/* Note Editor */}
              {showNoteEditor ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Button size="sm" variant="ghost" onClick={closeNoteEditor} className="rounded-lg text-[var(--isp-body)]">
                      <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    <h3 className="font-medium text-sm">{editingNote ? 'Edit Note' : 'New Note'}</h3>
                    {/* Real-time connection indicator */}
                    {liveEditingNoteId && (
                      <div className="flex items-center gap-1.5 ml-auto" data-testid="ws-status">
                        {collab.isConnected ? (
                          <span className="flex items-center gap-1 text-[10px] text-green-400 px-2 py-0.5 rounded-full bg-green-500/10">
                            <Wifi className="w-3 h-3" /> Live
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-[10px] text-amber-400 px-2 py-0.5 rounded-full bg-amber-500/10">
                            <WifiOff className="w-3 h-3" /> Connecting...
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Active editors */}
                  {liveEditingNoteId && collab.editors.length > 1 && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-purple-500/10 border border-purple-500/20" data-testid="active-editors">
                      <div className="flex -space-x-2">
                        {collab.editors.map((e, i) => (
                          <div key={e.user_id} className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-[10px] font-bold text-white border-2 border-slate-900" title={e.name}>
                            {e.name?.[0]?.toUpperCase() || '?'}
                          </div>
                        ))}
                      </div>
                      <span className="text-[11px] text-purple-300">
                        {collab.editors.length} editing
                      </span>
                    </div>
                  )}

                  {/* Typing indicators */}
                  {collab.typingUsers.length > 0 && (
                    <div className="flex items-center gap-2 text-[11px] text-[var(--isp-muted)] px-1" data-testid="typing-indicator">
                      <div className="flex gap-0.5">
                        <Circle className="w-1.5 h-1.5 fill-purple-400 text-purple-400 animate-bounce" style={{animationDelay:'0ms'}} />
                        <Circle className="w-1.5 h-1.5 fill-purple-400 text-purple-400 animate-bounce" style={{animationDelay:'150ms'}} />
                        <Circle className="w-1.5 h-1.5 fill-purple-400 text-purple-400 animate-bounce" style={{animationDelay:'300ms'}} />
                      </div>
                      {collab.typingUsers.map(u => u.user_name).join(', ')} typing...
                    </div>
                  )}

                  <Input
                    value={noteForm.title}
                    onChange={(e) => handleLiveTitleChange(e.target.value)}
                    placeholder="Note title..."
                    className="rounded-xl font-medium"
                    data-testid="note-title-input"
                  />
                  <textarea
                    value={liveEditingNoteId && collab.isConnected && collab.content !== noteForm.content && !document.activeElement?.getAttribute('data-testid')?.includes('note-content')
                      ? collab.content
                      : noteForm.content}
                    onChange={(e) => handleLiveContentChange(e.target.value)}
                    onFocus={() => liveEditingNoteId && collab.sendTyping(true)}
                    onBlur={() => liveEditingNoteId && collab.sendTyping(false)}
                    placeholder="Write your study notes here... (Markdown supported)"
                    rows={10}
                    className="w-full p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-heading)] text-sm resize-none"
                    data-testid="note-content-input"
                  />
                  {/* Tags */}
                  <div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {noteForm.tags.map((t, i) => (
                        <span key={`tag-${i}`} className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-300 flex items-center gap-1">
                          {t}
                          <button onClick={() => setNoteForm({ ...noteForm, tags: noteForm.tags.filter((_, idx) => idx !== i) })} className="text-[var(--isp-muted)] hover:text-red-400">&times;</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        placeholder="Add tag..."
                        className="flex-1 rounded-lg text-sm h-8"
                        onKeyPress={(e) => e.key === 'Enter' && addTag()}
                        data-testid="note-tag-input"
                      />
                      <Button size="sm" variant="outline" onClick={addTag} className="rounded-lg h-8">
                        <Tag className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <Button onClick={handleSaveNote} disabled={savingNote} className="rounded-xl gap-2 bg-purple-500 hover:bg-purple-600" data-testid="save-note-btn">
                    {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />}
                    {editingNote ? 'Update Note' : 'Create Note'}
                  </Button>
                </div>
              ) : viewingNote ? (
                /* View Single Note */
                <div className="space-y-3" data-testid="note-detail">
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setViewingNote(null)} className="rounded-lg text-[var(--isp-body)]">
                      <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl border border-[var(--isp-glass-border)]">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-[var(--isp-heading)]">{viewingNote.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          By {viewingNote.created_by_name} | Last edited by {viewingNote.last_edited_by_name}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openNoteEditor(viewingNote)} className="rounded-lg text-blue-400" data-testid="edit-note-btn">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {(isOwner || viewingNote.created_by === currentUserId) && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteNote(viewingNote.id)} className="rounded-lg text-red-400" data-testid="delete-note-btn">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {viewingNote.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {viewingNote.tags.map((t, i) => (
                          <span key={`t-${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300">{t}</span>
                        ))}
                      </div>
                    )}
                    <div className="text-sm text-[var(--isp-body)] whitespace-pre-wrap leading-relaxed">
                      {viewingNote.content || 'No content yet.'}
                    </div>
                  </div>
                  {viewingNote.edit_history?.length > 0 && (
                    <div className="text-xs text-[var(--isp-muted)] space-y-1">
                      <p className="font-medium text-[var(--isp-body)]">Edit History</p>
                      {viewingNote.edit_history.slice(-5).reverse().map((h, i) => (
                        <p key={`hist-${i}`}>{h.user_name} {h.action} - {new Date(h.at).toLocaleString()}</p>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Notes List */
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button onClick={() => openNoteEditor()} className="rounded-xl gap-2 bg-purple-500 hover:bg-purple-600 flex-1 sm:flex-none" data-testid="create-note-btn">
                      <Plus className="w-4 h-4" /> New Note
                    </Button>
                    {notes.length > 0 && (
                      <Button onClick={handleAiSummary} disabled={generatingSummary} variant="outline" className="rounded-xl gap-2 border-[var(--isp-glass-border)] text-[var(--isp-body)]" data-testid="ai-summary-btn">
                        {generatingSummary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-amber-400" />}
                        AI Summary
                      </Button>
                    )}
                  </div>

                  {/* AI Summary Card */}
                  {aiSummary?.summary && (
                    <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl" data-testid="ai-summary-card">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        <p className="text-sm font-medium text-amber-300">AI Summary ({aiSummary.notes_count} notes)</p>
                        <span className="text-[10px] text-[var(--isp-muted)] ml-auto">{aiSummary.generated_at?.slice(0, 10)}</span>
                      </div>
                      <div className="text-sm text-[var(--isp-body)] whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                        {aiSummary.summary}
                      </div>
                    </div>
                  )}

                  {notes.length === 0 ? (
                    <div className="text-center py-8">
                      <StickyNote className="w-10 h-10 mx-auto text-white/20 mb-3" />
                      <p className="text-muted-foreground">No shared notes yet</p>
                      <p className="text-xs text-muted-foreground mt-1">Create a note to start collaborating!</p>
                    </div>
                  ) : (
                    notes.map((note) => (
                      <button
                        key={note.id}
                        onClick={() => handleViewNote(note.id)}
                        className="w-full text-left p-4 bg-white/5 hover:bg-[var(--isp-glass-inner)] rounded-xl transition-all border border-transparent hover:border-purple-500/30"
                        data-testid={`note-${note.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <StickyNote className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[var(--isp-heading)] truncate">{note.title}</p>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {note.content?.slice(0, 100) || 'Empty note'}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-[10px] text-[var(--isp-muted)]">
                              <span className="flex items-center gap-1">
                                <Edit2 className="w-3 h-3" /> {note.last_edited_by_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {new Date(note.updated_at).toLocaleDateString()}
                              </span>
                            </div>
                            {note.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {note.tags.map((t, i) => (
                                  <span key={`tag-${i}`} className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/10 text-purple-300/70">{t}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-white/20 flex-shrink-0" />
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {/* Invite Section */}
              {isOwner && (
                <div className="p-3 bg-purple-500/10 rounded-xl mb-4">
                  <p className="text-sm font-medium mb-2">Invite Members</p>
                  <div className="flex gap-2">
                    <Input
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="Enter email address"
                      className="flex-1 rounded-lg"
                    />
                    <Button onClick={inviteMember} size="sm" className="rounded-lg">
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Members List */}
              {group.members?.map((member, idx) => (
                <div key={idx} className="p-3 bg-white/5 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                      {member.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    member.role === 'owner' ? 'bg-yellow-500/20 text-yellow-400' :
                    member.role === 'admin' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-white/10 text-muted-foreground'
                  }`}>
                    {member.role}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat Input */}
        {activeTab === 'chat' && (
          <div className="p-4 border-t border-[var(--isp-glass-border)]">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 rounded-xl"
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              />
              <Button onClick={sendMessage} className="rounded-xl bg-purple-500 hover:bg-purple-600" aria-label="Send group message" data-testid="group-msg-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default StudyGroupsPage;
